import { Dimension2D, Rectangle } from '../../../../types/geo.types';
import { GeoUtils } from '../../../../utils/geo.utils';
import { isInteger } from '../../../../utils/lodash-min';
import { Logger } from '../../../../models/logger';
import { CFile } from './cfile';
import { GLFXCanvas, GLFXFilterConfig, GLFXFilterValueMap } from './filter.contants';

declare var fx;
declare var WebPEncoder;
declare var WebPDecoder;
declare var CanvasToTIFF;
declare var UTIF;
export class CImage {

    private static readonly logger = Logger.getLogger('CImage');

    /**
   Callback will be called with the image once its ready
**/
    public static async resize(src_image: HTMLImageElement, src_area: Rectangle, dest_area: Rectangle): Promise<HTMLImageElement> {
        let canvas = document.createElement('canvas');
        // Source Image dimension
        const html_element_to_actual_image_width_ratio = src_image.naturalWidth / src_area.width;
        const html_element_to_actual_image_height_ratio = src_image.naturalHeight / src_area.height;
        const target_image_size = {
            width: dest_area.width * html_element_to_actual_image_width_ratio,
            height: dest_area.height * html_element_to_actual_image_height_ratio,
        };

        canvas.width = target_image_size.width;
        canvas.height = target_image_size.height;
        let ctx = canvas.getContext('2d');
        ctx.drawImage(src_image,
            0,
            0,
            src_image.naturalWidth,
            src_image.naturalHeight,
            0,
            0,
            target_image_size.width,
            target_image_size.height,
        );
        return await new Promise<HTMLImageElement>((resolve, reject) => {
            canvas.toBlob(
                (blob) => {
                    let image = new Image();
                    image.onload = () => {
                        resolve(image);
                        canvas.remove();
                    };
                    image.onerror = () => {
                        reject();
                    };
                    image.src = URL.createObjectURL(blob);
                },
                'image/png',
            );

        });
    }

    /**
        Callback will be called with the image once its ready
    **/
    public static async crop(image: HTMLImageElement, parentArea, cropArea): Promise<HTMLImageElement> {
        // calculate ratios
        let image_width = image.naturalWidth;
        let image_height = image.naturalHeight;

        let ratio_w = image_width / parentArea.width;
        let ratio_h = image_height / parentArea.height;

        let image_crop_top = Math.round(cropArea.top * ratio_h);
        let image_crop_left = Math.round(cropArea.left * ratio_w);
        let image_crop_width = Math.round(cropArea.width * ratio_w);
        let image_crop_height = Math.round(cropArea.height * ratio_h);

        let canvas = document.createElement('canvas');
        canvas.width = image_crop_width;
        canvas.height = image_crop_height;
        let ctx = canvas.getContext('2d');
        ctx.drawImage(image,
            image_crop_left,
            image_crop_top,
            image_crop_width,
            image_crop_height,
            0,
            0,
            image_crop_width,
            image_crop_height,
        );
        return await new Promise<HTMLImageElement>((resolve) => {
            canvas.toBlob(
                (blob) => {
                    let image = new Image();
                    image.onload = () => {
                        resolve(image);
                    };
                    image.src = URL.createObjectURL(blob);
                },
                'image/png',
            );
        });
    }

    public static async cloneImage(image: HTMLImageElement) {
        let canvas = document.createElement('canvas');
        canvas.width = image.naturalWidth;
        canvas.height = image.naturalHeight;
        let ctx = canvas.getContext('2d');
        ctx.drawImage(image,
            0,
            0,
            image.naturalWidth,
            image.naturalHeight,
        );
        return await new Promise<HTMLImageElement>((resolve, reject) => {
            canvas.toBlob(
                (blob) => {
                    let image = new Image();
                    image.onload = () => {
                        resolve(image);
                    };
                    image.onerror = () => {
                        reject();
                    };
                    image.src = URL.createObjectURL(blob);
                },
                'image/png',
                1,
            );
        });
    }

    /**
     *
     * @param canvas
     * @param type default is png, values are jpg or png
     */
    public static async canvasToImage(canvas: HTMLCanvasElement, type?: string): Promise<HTMLImageElement> {
        return await new Promise<HTMLImageElement>((resolve, reject) => {
            canvas.toBlob(
                (blob) => {
                    let image2 = new Image();
                    image2.onload = ($that) => {
                        resolve(image2);
                    };
                    image2.onerror = () => {
                        reject();
                    };
                    image2.src = URL.createObjectURL(blob);
                },
                `image/${type || 'png'}` // TODO change eveything to png
                ,
                1,
            );
        });
    }

    /**
     *
     * @param canvas
     * @param type default is png, values are jpg or png
     */
    public static async canvasToBlob(canvas: HTMLCanvasElement, type?: string): Promise<Blob> {
        return await new Promise<Blob>((resolve, reject) => {
            canvas.toBlob(
                (blob) => {
                    resolve(blob);
                },
                `image/${type || 'png'}` // TODO change eveything to png
                ,
                1,
            );
        });
    }

    public static async canvasToURLString(canvas: HTMLCanvasElement, type?: string): Promise<string> {
        return await new Promise<string>((resolve, reject) => {
            canvas.toBlob(
                (blob) => {
                    resolve(URL.createObjectURL(blob));
                },
                `image/${type || 'png'}` // TODO change eveything to png
                ,
                1,
            );
        });
    }

    public static async loadImageSrc(image: HTMLImageElement, src: string) {
        return await new Promise((resolve) => {
            image.onload = () => {
                resolve(image);
            };
            image.src = src;
        });
    }

    public static drawImageContained(canvas: HTMLCanvasElement, image: HTMLImageElement): HTMLCanvasElement {
        const context = canvas.getContext('2d');
        context.clearRect(0, 0, canvas.width, canvas.height);
        context.save();

        const rect = GeoUtils.coverAbsolutlyPositionedChild(
            {
                top: 0,
                left: 0,
                width: canvas.width,
                height: canvas.height,
            },
            {
                top: 0,
                left: 0,
                width: image.naturalWidth,
                height: image.naturalHeight,
            },
        );
        context.drawImage(image, 0, 0, image.naturalWidth, image.naturalHeight, rect.left, rect.top, rect.width, rect.height);
        context.restore();
        return canvas;
    }

    public static drawCanvasContained(targetCanvas: HTMLCanvasElement, sourceCanvas: HTMLCanvasElement): HTMLCanvasElement {
        const context = targetCanvas.getContext('2d');
        context.clearRect(0, 0, targetCanvas.width, targetCanvas.height);
        context.save();

        const rect = GeoUtils.coverAbsolutlyPositionedChild(
            {
                top: 0,
                left: 0,
                width: targetCanvas.width,
                height: targetCanvas.height,
            },
            {
                top: 0,
                left: 0,
                width: sourceCanvas.width,
                height: sourceCanvas.height,
            },
        );
        context.drawImage(sourceCanvas, 0, 0, sourceCanvas.width, sourceCanvas.height, rect.left, rect.top, rect.width, rect.height);
        context.restore();
        return targetCanvas;
    }

    public static drawImageScale(canvas: HTMLCanvasElement, image: HTMLImageElement, scaleX: number, scaleY: number) {
        const context = canvas.getContext('2d');
        context.clearRect(0, 0, canvas.width, canvas.height);
        context.save();
        context.scale(scaleX, scaleY);
        if (scaleX === 1 && scaleY === 1) {
            context.drawImage(image, 0, 0, canvas.width, canvas.height);
        } else if (scaleX === 1 && scaleY === -1) {
            context.drawImage(image, 0, -1 * canvas.height, canvas.width, canvas.height);
        } else if (scaleX === -1 && scaleY === 1) {
            context.drawImage(image, -1 * canvas.width, 0, canvas.width, canvas.height);
        } else if (scaleX === -1 && scaleY === -1) {
            context.drawImage(image, -1 * canvas.width, -1 * canvas.height, canvas.width, canvas.height);
        }
        context.restore();
    }

    public static drawImageRotate(canvas: HTMLCanvasElement, image: HTMLImageElement, degrees: number) {
        const context = canvas.getContext('2d');
        context.clearRect(0, 0, canvas.width, canvas.height);
        context.save();
        context.rotate(degrees * Math.PI / 180);
        if (degrees === 90) {
            context.drawImage(image, 0, 0, image.naturalWidth, image.naturalHeight, 0, -1 * canvas.width, canvas.height, canvas.width);
        } else if (degrees === 180) {
            context.drawImage(image, 0, 0, image.naturalWidth, image.naturalHeight, -1 * canvas.width, -1 * canvas.height, canvas.width, canvas.height);
        } else if (degrees === 270) {
            context.drawImage(image, 0, 0, image.naturalWidth, image.naturalHeight, -1 * canvas.height, 0, canvas.height, canvas.width);
        } else if (degrees === 360 || degrees === 0) {
            context.drawImage(image, 0, 0, image.naturalWidth, image.naturalHeight, -0, 0, canvas.width, canvas.height);
        }
        context.restore();
    }

    public static getImageDimension(image: HTMLImageElement): Dimension2D {
        return {
            width: image.naturalWidth,
            height: image.naturalHeight,
        };
    }

    public static isWidthGreater(image: HTMLImageElement) {
        return image.naturalWidth > image.naturalHeight;
    }

    public static getRelativeHeightFor(image: HTMLImageElement, targetWidth: number) {
        return image.naturalHeight * targetWidth / image.naturalWidth;
    }

    public static getRelativeWidthFor(image: HTMLImageElement, targetHeight: number) {
        return image.naturalWidth * targetHeight / image.naturalHeight;
    }

    public static getRelativeNearestIntegerHeightFor(canvas: HTMLCanvasElement, targetWidth: number): Dimension2D {
        let targetHeight = canvas.height * targetWidth / canvas.width;
        const defaultReturnValue = {
            width: targetWidth,
            height: targetHeight,
        };
        if (isInteger(targetHeight)) {
            return {
                width: targetWidth,
                height: targetHeight,
            };
        }
        const scanDistance = 400;
        const start = targetWidth < scanDistance ? targetWidth : targetWidth - scanDistance;

        const array: Dimension2D[] = [];
        const end = targetWidth + (scanDistance * 2);
        for (let i = start; i < end; i++) {
            targetHeight = canvas.height * i / canvas.width;
            if (isInteger(targetHeight)) {
                array.push({
                    width: i,
                    height: targetHeight,
                });
            }
        }

        if (array.length > 0) {
            let maxDiff = Number.MAX_SAFE_INTEGER;
            let value = null;
            array.forEach(val => {
                const diff = Math.abs(val.width - targetWidth);
                if (diff < maxDiff) {
                    maxDiff = diff;
                    value = val;
                }
            });
            if (value) {
                return value;
            }
        }

        return defaultReturnValue;

    }

    public static getRelativeNearestIntegerWidthFor(image: HTMLImageElement, targetHeight: number) {
        let targetWidth = image.naturalWidth * targetHeight / image.naturalHeight;

        const defaultReturnValue = {
            width: targetWidth,
            height: targetHeight,
        };
        if (isInteger(targetWidth)) {
            return {
                width: targetWidth,
                height: targetHeight,
            };
        }

        const scanDistance = 400;
        const start = targetHeight < scanDistance ? targetHeight : targetHeight - scanDistance;
        const end = targetHeight + (scanDistance * 2);
        const array = [];
        for (let i = start; i < end; i++) {
            targetWidth = image.naturalWidth * i / image.naturalHeight;
            if (isInteger(targetWidth)) {
                array.push({
                    width: targetWidth,
                    height: i,
                });
            }
        }
        if (array.length > 0) {
            let maxDiff = Number.MAX_SAFE_INTEGER;
            let value = null;
            array.forEach(val => {
                const diff = Math.abs(val.height - targetHeight);
                if (diff < maxDiff) {
                    maxDiff = diff;
                    value = val;
                }
            });
            if (value) {
                return value;
            }
        }

        return defaultReturnValue;
    }

    /**
     * Redundant since we have a applyFilter(...) function
     * @param canvas
     * @param image
     * @param config
     */
    public static async applyFilterToCanvas(canvas: GLFXCanvas, image: HTMLImageElement, config: GLFXFilterConfig) {
        const texture = canvas.texture(image);
        let c = canvas.draw(texture);
        if (config.blur > 0) {
            c = c.triangleBlur(config.blur);
        } else {
            c = c.unsharpMask(3, -1 * config.blur / 20);
        }

        if (config.brightness || config.contrast) {
            c = c.brightnessContrast(config.brightness / 100, config.contrast / 100);
        }
        if (config.saturation) {
            c = c.saturation(config.saturation);
        }
        if (config.vignette) {
            c = c.vignette(.1, config.vignette / 100);
        }
        if (config.xpro) {
            let cur = GLFXFilterValueMap.curves['' + config.xpro];
            c = c.curves(cur.red, cur.green, cur.blue);
        }

        if (config.tint) {
            let l = GLFXFilterValueMap.tint['' + config.tint];
            c.colorize(l.color.r, l.color.g, l.color.b, l.amount / 100);
        }

        c.update();
    }

    public static async loadCanvasWithWebPImage(webPFile: File, targetCanvas: HTMLCanvasElement) {
        const blob = await CFile.readFile(webPFile);
        const start = new Date();
        const WebPImage = { width: { value: 0 }, height: { value: 0 } };
        const decoder = new WebPDecoder();
        const bitmap = decoder.WebPDecodeRGB(blob, (blob as any).length, WebPImage.width, WebPImage.height);

        if (bitmap) {
            // Draw Image
            const start = new Date();
            const biHeight = WebPImage.height.value;
            const biWidth = WebPImage.width.value;

            targetCanvas.height = biHeight;
            targetCanvas.width = biWidth;

            const context = targetCanvas.getContext('2d');
            const output = context.createImageData(targetCanvas.width, targetCanvas.height);
            const outputData = output.data;

            for (let h = 0; h < biHeight; h++) {
                for (let w = 0; w < biWidth; w++) {
                    outputData[2 + w * 4 + (biWidth * 4) * h] = bitmap[2 + w * 3 + (biWidth * 3) * h];
                    outputData[1 + w * 4 + (biWidth * 4) * h] = bitmap[1 + w * 3 + (biWidth * 3) * h];
                    outputData[0 + w * 4 + (biWidth * 4) * h] = bitmap[0 + w * 3 + (biWidth * 3) * h];
                    outputData[3 + w * 4 + (biWidth * 4) * h] = 255;

                }
            }
            context.putImageData(output, 0, 0);
        }
    }

    private static canvasToBPMAsArrayBuffer(canvas: HTMLCanvasElement) {

        let w = canvas.width,
            h = canvas.height,
            w4 = w * 4,
            idata = canvas.getContext('2d').getImageData(0, 0, w, h),
            data32 = new Uint32Array(idata.data.buffer), // 32-bit representation of canvas

            stride = Math.floor((32 * w + 31) / 32) * 4, // row length incl. padding
            pixelArraySize = stride * h,                 // total bitmap size
            fileLength = 122 + pixelArraySize,           // header size is known + bitmap

            file = new ArrayBuffer(fileLength),          // raw byte buffer (returned)
            view = new DataView(file),                   // handle endian, reg. width etc.
            pos = 0, x, y = 0, p, s = 0, a, v;

        // write file header
        pos = CImage.setU16(view, pos, 0x4d42);          // BM
        pos = CImage.setU32(view, pos, fileLength);      // total length
        pos += 4;                // skip unused fields
        pos = CImage.setU32(view, pos, 0x7a);            // offset to pixels

        // DIB header
        pos = CImage.setU32(view, pos, 108);             // header size
        pos = CImage.setU32(view, pos, w);
        pos = CImage.setU32(view, pos, -h >>> 0);        // negative = top-to-bottom
        pos = CImage.setU16(view, pos, 1);               // 1 plane
        pos = CImage.setU16(view, pos, 32);              // 32-bits (RGBA)
        pos = CImage.setU32(view, pos, 3);               // no compression (BI_BITFIELDS, 3)
        pos = CImage.setU32(view, pos, pixelArraySize);  // bitmap size incl. padding (stride x height)
        pos = CImage.setU32(view, pos, 2835);            // pixels/meter h (~72 DPI x 39.3701 inch/m)
        pos = CImage.setU32(view, pos, 2835);            // pixels/meter v
        pos += 8;                // skip color/important colors
        pos = CImage.setU32(view, pos, 0xff0000);        // red channel mask
        pos = CImage.setU32(view, pos, 0xff00);          // green channel mask
        pos = CImage.setU32(view, pos, 0xff);            // blue channel mask
        pos = CImage.setU32(view, pos, 0xff000000);      // alpha channel mask
        pos = CImage.setU32(view, pos, 0x57696e20);      // " win" color space

        // bitmap data, change order of ABGR to BGRA
        while (y < h) {
            p = 0x7a + y * stride; // offset + stride x height
            x = 0;
            while (x < w4) {
                v = data32[s++];                     // get ABGR
                a = v >>> 24;                        // alpha channel
                view.setUint32(p + x, (v << 8) | a); // set BGRA
                x += 4;
            }
            y++;
        }

        return file;
    }

    public static canvasToBMPBlob(canvas: HTMLCanvasElement) {
        return new Blob([CImage.canvasToBPMAsArrayBuffer(canvas)], {
            type: 'image/bmp',
        });
    }

    public static canvasToBMPDataURL(canvas: HTMLCanvasElement) {
        let buffer = new Uint8Array(CImage.canvasToBPMAsArrayBuffer(canvas)),
            bs = '', i = 0, l = buffer.length;
        while (i < l) { bs += String.fromCharCode(buffer[i++]); }
        return 'data:image/bmp;base64,' + btoa(bs);
    }

    private static setU16(view, pos, data) {
        view.setUint16(pos, data, true);
        pos += 2;
        return pos;
    }
    private static setU32(view, pos, data) {
        view.setUint32(pos, data, true);
        pos += 4;
        return pos;
    }

    public static canvasToTIFFBlob(canvas): Promise<string> {
        // Convert canvas to TIFF as an Object URL
        return new Promise(resolve => {
            CanvasToTIFF.toBlob(canvas, (url) => {
                resolve(url);
            });
        });
    }

    public static async loadCanvasWithTiffImage(file: File, canvas: HTMLCanvasElement) {
        const buff: ArrayBuffer = await new Promise<HTMLImageElement>((resolve, reject) => {
            let fileReader = new FileReader();
            fileReader.onload = (evt: any) => {
                resolve(evt.target.result);
            };
            fileReader.readAsArrayBuffer(file);
        }) as any;

        // Guard against ANY throw from the third-party UTIF decode path
        // (vendored public/js/lib/UTIF.min.min.js — not modified here).
        // Production has hit `RangeError: Invalid array length` inside
        // UTIF's `_readIFD` for certain corrupt/oversized-IFD TIFFs, which
        // otherwise hard-crashes the editor. We have no reproducing file
        // yet, so on failure we ship a `TIFF decode failed`-prefixed report
        // with enough metadata (name, size, magic bytes) to find one.
        let page: any;
        let rgba: any;
        try {
            let ifds = UTIF.decode(buff);  // console.log(ifds);
            let vsns = ifds, ma = 0; page = vsns[0]; if (ifds[0].subIFD) { vsns = vsns.concat(ifds[0].subIFD); }
            for (let i = 0; i < vsns.length; i++) {
                let img = vsns[i];
                if (img['t258'] == null || img['t258'].length < 3) { continue; }
                let ar = img['t256'] * img['t257'];
                if (ar > ma) { ma = ar; page = img; }
            }
            UTIF.decodeImage(buff, page, ifds);
            rgba = UTIF.toRGBA8(page);
        } catch (err) {
            CImage.reportTiffDecodeFailure(file, buff, err);
            throw new Error('This TIFF file could not be loaded. It may be corrupted or use an unsupported format.');
        }

        const w = page.width;
        const h = page.height;
        canvas.width = w;
        canvas.height = h;
        let ctx = canvas.getContext('2d');
        const imgd = ctx.createImageData(w, h);
        for (let i = 0; i < rgba.length; i++) {
            imgd.data[i] = rgba[i];
        }
        ctx.putImageData(imgd, 0, 0);
    }

    /**
     * Repro-capture instrumentation for the UTIF crash guard above. We have
     * no reproducing file for the production `RangeError: Invalid array
     * length` crash, so on decode failure we ship everything cheaply
     * available pre-decode: file name, byte size, and the first-8-bytes hex
     * magic (already in memory in `buff`, no extra I/O). Never lets a
     * telemetry failure mask the original decode error.
     */
    private static reportTiffDecodeFailure(file: File, buff: ArrayBuffer, err: unknown) {
        try {
            const error = err instanceof Error ? err : new Error(String(err));
            const magicHex = CImage.getMagicHex(buff);
            CImage.logger.error({
                message: `TIFF decode failed: ${error.message}`,
                error,
                data: {
                    fileName: file?.name,
                    fileSize: file?.size,
                    magicHex,
                },
            });
        } catch (loggingErr) {
            // Instrumentation must never mask the original decode error.
            console.error('Failed to report TIFF decode failure', loggingErr);
        }
    }

    private static getMagicHex(buff: ArrayBuffer, numBytes = 8): string | undefined {
        try {
            if (!buff || !buff.byteLength) { return undefined; }
            const bytes = new Uint8Array(buff.slice(0, numBytes));
            return Array.from(bytes).map(b => b.toString(16).padStart(2, '0')).join('');
        } catch {
            return undefined;
        }
    }
}
